import { useEffect, useState } from "react";

import {
  ArrowButton,
  TextField,
  Checkbox,
  Select,
  Button,
  AutocompleteMultipleSelect,
  TableMetadata,
  TableStateWithFilters,
  useTable,
  Label,
  ColumnSortEnum,
  Spinner,
} from "@king-ict/eupisi-ui/components";
import { PagedResult, CodeBook } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import styles from "./SchoolCandidates.module.scss";
import {
  searchMunicipalities,
  searchTowns,
  searchStreets,
} from "../../api/Geography/GeographyApi";
import { searchInstitutionNew } from "../../api/Institution/InstitutionApi";
import {
  getSchoolCandidateFilteredList,
  setInstitutionAssignment,
  saveInstitutionAssignment,
  getSchoolCandidateListExport,
} from "../../api/SchoolCandidate/SchoolCandidateApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  InstitutionSelect,
  ListTable,
  SearchCard,
  ToolbarTop,
} from "../../components";
import { ConfirmModal } from "../../components/FormControls/ConfirmModal";
import { useAuthCounties, useDidUpdateEffect, useInput } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import {
  HierarchicalCodeBookPagedFilter,
  HierarchicalCodeBook,
  SchoolYear,
  ValidationFailure,
  GrandParentCodeBookPagedFilter,
  GreatGrandParentCodeBookPagedFilter,
  InstitutionCodeBook,
  Role,
  Selectable,
} from "../../models";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { InstitutionCodeBookSearch } from "../../models/Institution";
import {
  SchoolCandidate,
  SchoolCandidateSearch,
  HouseNumberSelector,
  SaveSchoolCandidateInstitutionAssignment,
  SchoolCandidateSort,
} from "../../models/SchoolCandidate";
import { ClaimType } from "../../models/User";
import {
  useGetInstitutionListForUserQuery,
  useGetSchoolCandidateStatusListQuery,
  useGetSchoolYearCurrentQuery,
  useGetSchoolYearListQuery,
} from "../../queries/codeBookQuery";
import {
  schoolCandidateDetailUrl,
  schoolCandidateEditUrl,
} from "../../routes/routes";
import { setRedirectURL } from "../../slices/applicationSlice";
import { FormatUtils, MsgUtils, TableUtils, ObjectUtils } from "../../utils";
import { useSchoolYearEditable } from "./hooks";

/**
 * Only Global and County level users.
 * @returns
 */
export function SchoolCandidateList() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { data: currentSchoolYear } = useGetSchoolYearCurrentQuery();
  const authCounties = useAuthCounties(); // Institution users cannot use this component.
  //STORAGE KEY
  const storageKey = "SchoolCandidateSearchTableState";
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const createInitialTableState = () => {
    return TableUtils.CreateInitialTableState<SchoolCandidateSearch>(
      storageKey,
      TableUtils.tableDefaultState(
        SchoolCandidateSort.FirstName,
        ColumnSortEnum.ASC,
        50
      )
    );
  };
  const [initialTableState] = useState<
    TableStateWithFilters<SchoolCandidateSearch>
  >(() => createInitialTableState());
  //INIT SEARCH FILTER
  const initialSchoolCandidateSearch: SchoolCandidateSearch = {
    schoolYearId: currentSchoolYear?.id,
    countyList: [],
    uoguCountyId:
      auth.currentRole?.claimType === ClaimType.County &&
      auth.currentRole.locations?.some((v) => true)
        ? auth.currentRole.locations[0]
        : undefined,
    municipalityList: [],
    townList: [],
    streetList: [],
    houseNumberFrom: undefined,
    houseNumberTo: undefined,
    houseNumberSelector: undefined,
    lastNamePart: undefined,
    firstNamePart: undefined,
    oib: undefined,
    institutionList: [],
    status: undefined,
    onlyWithNote: false,
    onlyWithAddReasonNotOnItitialMUPList: false,
    onlyWithAddReasonDelayPreviousYear: false,
    onlyWithTemporaryAddress: false,
    onlyUnassignedSchoolCandidates: false,
    onlyWithCancelReason: false,
  };
  const createInitialSchoolCandidateSearch = (): SchoolCandidateSearch => {
    return initialTableState?.onlyUnassignedSchoolCandidates !== undefined
      ? initialTableState
      : initialSchoolCandidateSearch;
  };

  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<
    SchoolCandidate & Selectable
  >();
  const [tableData, setTableData] =
    useState<PagedResult<SchoolCandidate & Selectable>>(defaultTableData);
  //SET SEARCH FILTERS
  const [schoolCandidateSearch, setSchoolCandidateSearch] =
    useState<SchoolCandidateSearch>(() => createInitialSchoolCandidateSearch());
  const [tableSearch, setTableSearch] = useState<SchoolCandidateSearch>(() =>
    createInitialSchoolCandidateSearch()
  );
  const [filterChanged, setFilterChanged] = useState(false);
  const [clearFilter, setClearFilter] = useState(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<SchoolCandidate & Selectable> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "select",
        header: "",
        accessor: (item: SchoolCandidate & Selectable) => {
          return (
            <div>
              <Checkbox
                label={""}
                name={nameof<typeof item>("id")}
                value={item.id}
                checked={item.isSelected}
                onChange={handleSchoolCandidateCheckedChange}
                disabled={!isSchoolYearEditable}
              />
            </div>
          );
        },
      },
      {
        columnKey: "firstName",
        header:
          t("common.schoolCandidateModel.lastName") +
          "/" +
          t("common.schoolCandidateModel.firstName"),
        accessor: (item: SchoolCandidate) => {
          return (
            <div>
              <div className={"text-nowrap"}>
                {item.lastName},&nbsp;{item.firstName}
              </div>
              <div className={"text-nowrap"}>
                {t("common.schoolCandidateModel.oib")}:&nbsp;{item.oib}
              </div>
            </div>
          );
        },
        sortBy: 1,
      },
      {
        columnKey: "dateOfBirth",
        header: t("common.schoolCandidateModel.dateOfBirth"),
        accessor: (item: SchoolCandidate) => {
          return (
            <div className={"text-nowrap text-right"}>
              {hrDate(new Date(item.dateOfBirth))}
            </div>
          );
        },
        sortBy: 2,
      },
      {
        columnKey: "permanentAddress",
        header:
          t("common.schoolCandidateModel.permanentAddress") +
          "/" +
          t("common.schoolCandidateModel.temporaryAddress"),
        accessor: (item: SchoolCandidate) => {
          return (
            <>
              <div className={"text-nowrap"}>
                <b>
                  {item.residence?.permanentAddress?.street}&nbsp;
                  {item.residence?.permanentAddress?.houseNumber},&nbsp;
                  {item.residence?.permanentAddress?.town}{" "}
                  {/*,&nbsp;{item.permanentAddress.municipality},&nbsp;{item.permanentAddress.county*/}
                </b>
              </div>
              {item.residence?.temporaryAddress && (
                <div className={"text-nowrap"}>
                  {item.residence?.temporaryAddress?.street}&nbsp;
                  {item.residence?.temporaryAddress.houseNumber},&nbsp;
                  {item.residence?.temporaryAddress.town}{" "}
                  {/*,&nbsp;{item.temporaryAddress.municipality},&nbsp;{item.temporaryAddress.county*/}
                </div>
              )}
            </>
          );
        },
        sortBy: 3,
      },
      {
        columnKey: "institution",
        header: t("common.schoolCandidateModel.institutionList"),
        accessor: (item: SchoolCandidate) => {
          return (
            <div>
              {institutions?.find((v) => v.id === item.institutionId)?.name}
            </div>
          );
        },
        sortBy: 4,
      },
      {
        columnKey: "permanentAddressNote",
        header: t("common.schoolCandidateModel.permanentAddressNote"),
        accessor: "permanentAddressNote",
        sortBy: 5,
      },
      {
        columnKey: "isHandicapped",
        header: t("common.schoolCandidateModel.isHandicapped"),
        accessor: (item: SchoolCandidate) => {
          return (
            <>
              <div className={"text-nowrap"}>
                {item.isHandicapped ? t("common.yes") : t("common.no")}
              </div>
            </>
          );
        },
      },
      {
        columnKey: "status",
        header: t("common.schoolCandidateModel.status"),
        accessor: (item: SchoolCandidate) => {
          return (
            <>
              <Label
                color={FormatUtils.getSchoolCandidateStatusColor(item.statusId)}
                className="text-center"
                outline={false}
                fullWidth={false}
              >
                {statuses?.find((v) => v.id === item.statusId)?.name}
              </Label>
            </>
          );
        },
        sortBy: 6,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: SchoolCandidate) => {
          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  /** The table metadata. The search is performed when the tableState changes (useEffect)
   * which is triggerd by a call to setFilters. Why are we doing this?
   */
  const [tableProps, tableState, setFilters] = useTable<SchoolCandidateSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: initialTableState.page,
    paginationPageSize: initialTableState.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: initialTableState.order,
      sortBy: initialTableState.sortBy,
    },
  });

  //FILTER DATA

  //EDIT DATA
  const { data: statuses } = useGetSchoolCandidateStatusListQuery();
  const { data: schoolYears } = useGetSchoolYearListQuery();
  const { data: institutions } = useGetInstitutionListForUserQuery();

  const [filteredInstitutions, setFilteredInstitutions] = useState<
    InstitutionCodeBook[]
  >([]);
  const [filteredMunicipalities, setFilteredMunicipalities] = useState<
    HierarchicalCodeBook[]
  >([]);
  const [filteredTowns, setFilteredTowns] = useState<HierarchicalCodeBook[]>(
    []
  );
  const [filteredStreets, setFilteredStreets] = useState<
    HierarchicalCodeBook[]
  >([]);
  const [houseNumberSelectors] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(HouseNumberSelector, "houseNumberSelector")
  );

  //EDIT DATA
  const createDefaultUpdate = (): SaveSchoolCandidateInstitutionAssignment => {
    return {
      institutionId: undefined,
      schoolCandidateList: [],
    };
  };
  const { isSchoolYearEditable, evaluateSchoolYearEditable } =
    useSchoolYearEditable({ schoolYears });
  const [confirmUnassign, setConfirmUnassign] = useState<any>();
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [addAssignmentEnabled, setAddAssignmentEnabled] = useState(false);
  const [updateInstitutionAssignment, setUpdateInstitutionAssignment] =
    useState<SaveSchoolCandidateInstitutionAssignment>(createDefaultUpdate());
  const [clearAssignmentEnabled, setClearAssignmentEnabled] = useState(false);
  const [
    addHouseNumberInstitutionEnabled,
    setAddHouseNumberInstitutionEnabled,
  ] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  useDidUpdateEffect(() => {
    //used when user refresh/reload page
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
    };
    if (
      currentSchoolYear &&
      schoolCandidateSearch.schoolYearId !== currentSchoolYear?.id &&
      schoolYears
    ) {
      newSchoolCandidateSearch.schoolYearId = currentSchoolYear?.id;
    }

    setSchoolCandidateSearch(newSchoolCandidateSearch);
    setTableSearch(newSchoolCandidateSearch);
    setClearFilter(true);
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSchoolYear, schoolYears]);

  useEffect(() => {
    console.debug(
      "init.schoolCandidateSearch",
      schoolCandidateSearch.schoolYearId
    );
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    if (schoolCandidateSearch.schoolYearId)
      evaluateSchoolYearEditable(schoolCandidateSearch.schoolYearId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolCandidateSearch]);

  useEffect(() => {
    if (!schoolCandidateSearch?.schoolYearId) return;
    (async () => {
      const request = TableUtils.GetSearchRequest<SchoolCandidateSearch>(
        tableSearch,
        tableState
      );
      const itemList = await getSchoolCandidateFilteredList(request);
      console.log("Api response: ", itemList);
      if (itemList !== null && itemList.results.length > 0) {
        const pagedResult: PagedResult<SchoolCandidate & Selectable> = {
          ...itemList,
          results: ObjectUtils.makeSelectableArray(itemList.results),
        };
        console.log("PagedResult: ", pagedResult);
        setTableData(pagedResult);
      } else {
        setTableData(defaultTableData);
      }
      // Any time we search, we need to clear the Update entity.
      setUpdateInstitutionAssignment(createDefaultUpdate());
      // Any time we clear the Update entity, we don't have anything selected.
      setSelectAll(false);
      clearAssigmentButtons();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  //FILTER HANDLERS
  const handleSchoolYearChange = (e: any) => {
    const schoolYear = Number.parseInt(e.target.value);
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      schoolYearId: schoolYear,
    };
    setSchoolCandidateSearch(newSchoolCandidateSearch);
    evaluateSchoolYearEditable(schoolYear);
  };

  const handleInstitutionFilterChange = async (filter: string) => {
    const search: InstitutionCodeBookSearch = {
      onlyActive: true,
      countyIds: schoolCandidateSearch.uoguCountyId
        ? [schoolCandidateSearch.uoguCountyId]
        : undefined,
      search: filter,
      includeOutsideEnrollmentArea: false,
      includeHandicapped: false,
      includePrivate: undefined, // both
      includeSatellite: undefined, // both
    };
    const result = await searchInstitutionNew(search);

    setFilteredInstitutions(result ?? []);
  };
  const handleMunicipalityFilterChange = async (filter: string) => {
    const search: HierarchicalCodeBookPagedFilter = {
      pageSize: 500,
      page: 1,
      ids: schoolCandidateSearch.municipalityList.map((val) => val.id),
      parentId: schoolCandidateSearch.countyList,
      search: filter,
    };
    const result = await searchMunicipalities(search);
    setFilteredMunicipalities(result.results);
  };
  const handleTownFilterChange = async (filter: string) => {
    const search: GrandParentCodeBookPagedFilter = {
      pageSize: 500,
      page: 1,
      ids: schoolCandidateSearch.townList.map((val) => val.id),
      parentId: schoolCandidateSearch.municipalityList.map((val) => val.id),
      grandParentId: schoolCandidateSearch.countyList,
      search: filter,
    };
    const result = await searchTowns(search);
    setFilteredTowns(result.results);
  };
  const handleStreetFilterChange = async (filter: string) => {
    const search: GreatGrandParentCodeBookPagedFilter = {
      pageSize: 500,
      page: 1,
      ids: schoolCandidateSearch.streetList.map((val) => val.id),
      parentId: schoolCandidateSearch.townList.map((val) => val.id),
      grandParentId: schoolCandidateSearch.municipalityList.map(
        (val) => val.id
      ),
      greatGrandParentId: schoolCandidateSearch.countyList,
      search: filter,
    };
    const result = await searchStreets(search);
    setFilteredStreets(result.results);
  };
  const handleHouseNumberFromChange = (e: any) => {
    const houseNumberFrom = Number.parseInt(e.target.value);
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      houseNumberFrom: !isNaN(houseNumberFrom) ? houseNumberFrom : undefined,
    };
    setSchoolCandidateSearch(newSchoolCandidateSearch);
  };
  const handleHouseNumberToChange = (e: any) => {
    const houseNumberTo = Number.parseInt(e.target.value);
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      houseNumberTo: !isNaN(houseNumberTo) ? houseNumberTo : undefined,
    };
    setSchoolCandidateSearch(newSchoolCandidateSearch);
  };
  const handleHouseNumberSelectorChange = (e: any) => {
    const houseNumberSelector = Number.parseInt(e.target.value);
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      houseNumberSelector:
        !isNaN(houseNumberSelector) && houseNumberSelector > 0
          ? houseNumberSelector
          : undefined,
    };
    setSchoolCandidateSearch(newSchoolCandidateSearch);
  };
  const handleCountyChange = (countyList: number[]) => {
    let municipalityList = schoolCandidateSearch.municipalityList.filter(
      (v) => countyList.find((c) => c === v.parentId) !== undefined
    );
    let townList = schoolCandidateSearch.townList.filter(
      (v) =>
        schoolCandidateSearch.municipalityList.find(
          (c) => c.id === v.parentId
        ) !== undefined
    );
    let streetList = schoolCandidateSearch.streetList.filter(
      (v) =>
        schoolCandidateSearch.townList.find((c) => c.id === v.parentId) !==
        undefined
    );
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      countyList: countyList,
      municipalityList: municipalityList,
      townList: townList,
      streetList: streetList,
    };
    setSchoolCandidateSearch(newSchoolCandidateSearch);
  };
  const handleMunicipalityChange = (e: any) => {
    const municipalityList = e.target.value as HierarchicalCodeBook[];
    let townList = schoolCandidateSearch.townList.filter(
      (v) => municipalityList.find((c) => c.id === v.parentId) !== undefined
    );
    let streetList = schoolCandidateSearch.streetList.filter(
      (v) =>
        schoolCandidateSearch.townList.find((c) => c.id === v.parentId) !==
        undefined
    );
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      municipalityList: municipalityList,
      townList: townList,
      streetList: streetList,
    };
    setSchoolCandidateSearch(newSchoolCandidateSearch);
  };
  const handleTownChange = (e: any) => {
    const townList = e.target.value as HierarchicalCodeBook[];
    let streetList = schoolCandidateSearch.streetList.filter(
      (v) => townList.find((c) => c.id === v.parentId) !== undefined
    );
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      townList: townList,
      streetList: streetList,
    };

    setSchoolCandidateSearch(newSchoolCandidateSearch);
  };
  const handleStreetChange = (e: any) => {
    const streetList = e.target.value as HierarchicalCodeBook[];
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      streetList: streetList,
    };
    setSchoolCandidateSearch(newSchoolCandidateSearch);
  };
  const handleUOGUCountyChange = (e: any) => {
    const county = Number.parseInt(e.target.value);
    const countyInstitutions = institutions?.filter(
      (val) => val.countyId === county
    );
    const newSchoolCandidateSearch: SchoolCandidateSearch = {
      ...schoolCandidateSearch,
      uoguCountyId: !isNaN(county) && county > 0 ? county : undefined,
      institutionList:
        schoolCandidateSearch.institutionList?.filter((v) =>
          countyInstitutions?.some((val) => val.id === v.id)
        ) ?? [],
    };
    setSchoolCandidateSearch(newSchoolCandidateSearch);
    if (
      updateInstitutionAssignment.institutionId &&
      countyInstitutions?.find(
        (val) => val.id === updateInstitutionAssignment.institutionId
      ) === undefined
    ) {
      handleAssignmentInstitutionChange(undefined);
    }
  };
  const handleDetailsClicked = (id?: number) => {
    dispatch(setRedirectURL(history.location.pathname));
    if (id) {
      TableUtils.SetSessionStorageItem<SchoolCandidateSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(schoolCandidateDetailUrl(id!));
    }
  };
  const handleNewSchoolCandidateClicked = () => {
    TableUtils.SetSessionStorageItem<SchoolCandidateSearch>(
      storageKey,
      tableSearch,
      tableState
    );
    dispatch(setRedirectURL(history.location.pathname));
    history.push(schoolCandidateEditUrl(0));
  };
  const handleClearSearchClicked = () => {
    setClearFilter(true);
    setSchoolCandidateSearch(initialSchoolCandidateSearch);
    setTableSearch(initialSchoolCandidateSearch);
    evaluateSchoolYearEditable(currentSchoolYear?.id);
    search();
  };
  const handleSearchClicked = (e: any) => {
    setTableSearch(schoolCandidateSearch);
    setFilterChanged(false);
    search();
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };
  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getSchoolCandidateListExport(schoolCandidateSearch, exportType);
  };
  //EDIT HANDLERS
  const handleAssignmentInstitutionChange = (institutionId?: number) => {
    const newUpdate: SaveSchoolCandidateInstitutionAssignment = {
      ...updateInstitutionAssignment,
      institutionId: institutionId,
    };
    setUpdateInstitutionAssignment(newUpdate);
    setAssignmentButtons(newUpdate);
  };
  const handleSchoolCandidateCheckedChange = (e: any) => {
    const schoolCandidateId = Number.parseInt(e.target.value);
    const isChecked = e.target.checked as boolean;
    if (schoolCandidateId) {
      const schoolCandidate = tableData.results.find(
        (val) => val.id === schoolCandidateId
      );
      let schoolCandidateList = updateInstitutionAssignment.schoolCandidateList;
      if (isChecked && schoolCandidate) {
        schoolCandidateList.push(schoolCandidateId);
        schoolCandidate!.isSelected = true;
      } else {
        schoolCandidateList = schoolCandidateList.filter(
          (val) => val !== schoolCandidateId
        );
        setSelectAll(false);
        schoolCandidate!.isSelected = false;
      }
      const newUpdateInstitutionAssignment: SaveSchoolCandidateInstitutionAssignment =
        {
          ...updateInstitutionAssignment,
          schoolCandidateList: schoolCandidateList,
        };
      setUpdateInstitutionAssignment(newUpdateInstitutionAssignment);
      setAssignmentButtons(newUpdateInstitutionAssignment);
    }
  };
  const setAssignmentButtons = (
    u: SaveSchoolCandidateInstitutionAssignment
  ) => {
    setAddAssignmentEnabled(
      u.schoolCandidateList.length > 0 &&
        !tableData.results.some(
          (v) =>
            u.schoolCandidateList.some((val) => val === v.id) && v.institutionId
        ) &&
        u.institutionId !== undefined
    );
    setClearAssignmentEnabled(
      u.schoolCandidateList.length > 0 &&
        tableData.results.some(
          (v) =>
            u.schoolCandidateList.some((val) => val === v.id) && v.institutionId
        )
    );
    setAddHouseNumberInstitutionEnabled(
      u.schoolCandidateList.length > 0 &&
        !tableData.results.some(
          (v) =>
            u.schoolCandidateList.some((val) => val === v.id) && v.institutionId
        ) &&
        u.institutionId === undefined
    );
  };
  const clearAssigmentButtons = () => {
    setSelectAll(false);
    setClearAssignmentEnabled(false);
    setAddAssignmentEnabled(false);
    setAddHouseNumberInstitutionEnabled(false);
  };
  const handleSelectAllCheckedChange = (e: any) => {
    const isChecked = !selectAll;
    setSelectAll(isChecked);
    const newResults = tableData.results.map((val) => {
      val.isSelected = isChecked;
      return val;
    });
    const newTableData: PagedResult<SchoolCandidate & Selectable> = {
      ...tableData,
      results: newResults,
    };
    const newUpdate: SaveSchoolCandidateInstitutionAssignment = {
      ...updateInstitutionAssignment,
      schoolCandidateList: newResults
        .filter((val) => val.isSelected)
        .map((v) => v.id),
    };
    setTableData(newTableData);
    setUpdateInstitutionAssignment(newUpdate);
    setAssignmentButtons(newUpdate);
  };
  const handleClearAssignmentsClicked = async () => {
    const save: SaveSchoolCandidateInstitutionAssignment = {
      ...updateInstitutionAssignment,
      institutionId: undefined,
    };
    const result = await saveInstitutionAssignment(save);
    if (result) {
      setErrors(result.validationFailures);
      if (result.isValid) setConfirmUnassign("message");
      search();
    } else {
      setConfirmUnassign("error");
    }
  };
  const handleAddAssignmentsClicked = async () => {
    const update: SaveSchoolCandidateInstitutionAssignment =
      updateInstitutionAssignment;
    const result = await saveInstitutionAssignment(update);
    if (result) {
      setErrors(result.validationFailures);
      search();
    }
  };
  const handleAssignHouseNumberInstitutionClicked = async () => {
    const save: SaveSchoolCandidateInstitutionAssignment = {
      ...updateInstitutionAssignment,
      institutionId: undefined,
    };
    const result = await setInstitutionAssignment(save);
    if (result) {
      setErrors(undefined);
      setErrors(result.validationFailures);
      window.scroll(0, 0);
      search();
    }
  };

  if (!schoolYears || !currentSchoolYear || !authCounties)
    return <Spinner show={true} />;
  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("navigation.schoolCandidateList")}
        showAdd={isSchoolYearEditable ? "show" : "disable"}
        onClickAdd={handleNewSchoolCandidateClicked}
        errors={errors}
      />
      <SearchCard
        onClickSearch={handleSearchClicked}
        onClickClearSearch={handleClearSearchClicked}
        isFilterChanged={filterChanged}
        hideDivider={true}
      >
        <Row>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.uoguCounty")}
              name={nameof<typeof schoolCandidateSearch>("uoguCountyId")}
              infoText={t("common.schoolCandidateModel.uoguCountyIdInfoText")}
              value={schoolCandidateSearch.uoguCountyId}
              onChange={handleUOGUCountyChange}
              selectData={codeBookToSelectData(authCounties)}
              emptyOption={
                auth.currentRole?.role === Role.CAS ||
                auth.currentRole?.role === Role.Helpdesk
                  ? { value: undefined, text: "" }
                  : undefined
              }
              disabled={authCounties.length === 1}
            />
          </Col>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.schoolYearId")}
              name={nameof<typeof schoolCandidateSearch>("schoolYearId")}
              value={schoolCandidateSearch.schoolYearId}
              required={true}
              onChange={handleSchoolYearChange}
              selectData={codeBookToSelectData(schoolYears)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Select
              multiple
              label={t("common.schoolCandidateModel.countyList")}
              name={nameof<typeof schoolCandidateSearch>("countyList")}
              infoText={t("common.schoolCandidateModel.countyListInfoText")}
              value={schoolCandidateSearch.countyList}
              onChange={(e) => handleCountyChange(e.target.value)}
              selectData={codeBookToSelectData(authCounties)}
            />
          </Col>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.lastName")}
              name={nameof<typeof schoolCandidateSearch>("lastNamePart")}
              value={schoolCandidateSearch.lastNamePart}
              onChange={handleChange(setSchoolCandidateSearch)}
            />
          </Col>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.firstName")}
              name={nameof<typeof schoolCandidateSearch>("firstNamePart")}
              value={schoolCandidateSearch.firstNamePart}
              onChange={handleChange(setSchoolCandidateSearch)}
            />
          </Col>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.oib")}
              name={nameof<typeof schoolCandidateSearch>("oib")}
              value={schoolCandidateSearch.oib}
              onChange={handleChange(setSchoolCandidateSearch)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <AutocompleteMultipleSelect<HierarchicalCodeBook>
              label={t("common.schoolCandidateModel.municipalityList")}
              name={nameof<typeof schoolCandidateSearch>("municipalityList")}
              value={schoolCandidateSearch.municipalityList}
              valueProp="id"
              textProp="name"
              onChange={handleMunicipalityChange}
              selectOptions={filteredMunicipalities}
              onFilterChange={handleMunicipalityFilterChange}
              maximumSelectedItems={10}
            />
          </Col>
          <Col md={6}>
            <AutocompleteMultipleSelect<InstitutionCodeBook>
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof schoolCandidateSearch>("institutionList")}
              value={
                institutions?.filter((v) =>
                  schoolCandidateSearch?.institutionList?.some(
                    (val) => val.id === v.id
                  )
                ) ?? []
              }
              valueProp="id"
              textProp="name"
              onChange={handleChange(setSchoolCandidateSearch)}
              selectOptions={filteredInstitutions}
              onFilterChange={handleInstitutionFilterChange}
              maximumSelectedItems={10}
            />
          </Col>
        </Row>
        <Row className={styles.rowPaddingMedium}>
          <Col md={6}>
            <AutocompleteMultipleSelect<HierarchicalCodeBook>
              label={t("common.schoolCandidateModel.townList")}
              name={nameof<typeof schoolCandidateSearch>("townList")}
              value={schoolCandidateSearch.townList}
              valueProp="id"
              textProp="name"
              onChange={handleTownChange}
              selectOptions={filteredTowns}
              onFilterChange={handleTownFilterChange}
              maximumSelectedItems={10}
            />
          </Col>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.status")}
              name={nameof<typeof schoolCandidateSearch>("status")}
              value={schoolCandidateSearch.status}
              onChange={handleChange(setSchoolCandidateSearch)}
              selectData={codeBookToSelectData(statuses)}
              emptyOption={{ value: undefined, text: t("common.all") }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <AutocompleteMultipleSelect<HierarchicalCodeBook>
              label={t("common.schoolCandidateModel.streetList")}
              name={nameof<typeof schoolCandidateSearch>("streetList")}
              value={schoolCandidateSearch.streetList}
              valueProp="id"
              textProp="name"
              onChange={handleStreetChange}
              selectOptions={filteredStreets}
              onFilterChange={handleStreetFilterChange}
              maximumSelectedItems={10}
            />
          </Col>
          <Col md={3}>
            <Checkbox
              label={t("common.schoolCandidateModel.onlyWithNote")}
              name={nameof<typeof schoolCandidateSearch>("onlyWithNote")}
              checked={schoolCandidateSearch.onlyWithNote}
              onChange={handleChange(setSchoolCandidateSearch)}
            />
          </Col>
          <Col md={3}>
            <Checkbox
              label={t("common.schoolCandidateModel.onlyWithTemporaryAddress")}
              name={nameof<typeof schoolCandidateSearch>(
                "onlyWithTemporaryAddress"
              )}
              checked={schoolCandidateSearch.onlyWithTemporaryAddress}
              onChange={handleChange(setSchoolCandidateSearch)}
            />
          </Col>
        </Row>
        <Row className={styles.rowPaddingMedium}>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.houseNumberFrom")}
              name={nameof<typeof schoolCandidateSearch>("houseNumberFrom")}
              value={schoolCandidateSearch.houseNumberFrom}
              onChange={handleHouseNumberFromChange}
            />
          </Col>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.houseNumberTo")}
              name={nameof<typeof schoolCandidateSearch>("houseNumberTo")}
              value={schoolCandidateSearch.houseNumberTo}
              onChange={handleHouseNumberToChange}
            />
          </Col>
          <Col md={2}>
            <Select
              label={t("common.schoolCandidateModel.houseNumberSelector")}
              name={nameof<typeof schoolCandidateSearch>("houseNumberSelector")}
              value={schoolCandidateSearch.houseNumberSelector}
              onChange={handleHouseNumberSelectorChange}
              selectData={codeBookToSelectData(houseNumberSelectors)}
              emptyOption={{ value: "", text: t("common.all") }}
            />
          </Col>
          <Col md={3}>
            <Checkbox
              label={t(
                "common.schoolCandidateModel.onlyWithAddReasonNotOnItitialMUPList"
              )}
              name={nameof<typeof schoolCandidateSearch>(
                "onlyWithAddReasonNotOnItitialMUPList"
              )}
              checked={
                schoolCandidateSearch.onlyWithAddReasonNotOnItitialMUPList
              }
              onChange={handleChange(setSchoolCandidateSearch)}
            />
          </Col>
          <Col md={3}>
            <Checkbox
              label={t(
                "common.schoolCandidateModel.onlyUnassignedSchoolCandidates"
              )}
              name={nameof<typeof schoolCandidateSearch>(
                "onlyUnassignedSchoolCandidates"
              )}
              checked={schoolCandidateSearch.onlyUnassignedSchoolCandidates}
              onChange={handleChange(setSchoolCandidateSearch)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}></Col>
          <Col md={3}>
            <Checkbox
              label={t(
                "common.schoolCandidateModel.onlyWithAddReasonDelayPreviousYear"
              )}
              name={nameof<typeof schoolCandidateSearch>(
                "onlyWithAddReasonDelayPreviousYear"
              )}
              checked={schoolCandidateSearch.onlyWithAddReasonDelayPreviousYear}
              onChange={handleChange(setSchoolCandidateSearch)}
            />
          </Col>
          <Col md={3}>
            <Checkbox
              label={t("common.schoolCandidateModel.onlyWithCancelReason")}
              name={nameof<typeof schoolCandidateSearch>(
                "onlyWithCancelReason"
              )}
              checked={schoolCandidateSearch.onlyWithCancelReason}
              onChange={handleChange(setSchoolCandidateSearch)}
            />
          </Col>
        </Row>
      </SearchCard>
      <FormCard
        cardTitle={t("common.schoolCandidateModel.schoolCandidateAssign")}
      >
        <Row className="m-1 pb-3 pt-3 bg-light border">
          <Col md={2} className="pt-4">
            <Checkbox
              label={t("common.selectAll")}
              name={"selectAll"}
              checked={selectAll}
              onChange={handleSelectAllCheckedChange}
              disabled={!isSchoolYearEditable}
            />
          </Col>
          <Col md={4}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof updateInstitutionAssignment>("institutionId")}
              value={updateInstitutionAssignment.institutionId}
              onlyByEnrollmentInstitution
              excludeHandicapped
              showUser
              onChange={(e) =>
                handleAssignmentInstitutionChange(e.target.value)
              }
              countyId={schoolCandidateSearch.uoguCountyId}
            />
          </Col>
          <Col md={2} className="pt-4">
            <Button
              onClick={() => setConfirmUnassign("confirm")}
              outline={true}
              size="sm"
              disabled={!clearAssignmentEnabled}
            >
              {t("common.schoolCandidateModel.clearAssignments")}
            </Button>
          </Col>
          <Col md={2} className="pt-4">
            <Button
              onClick={handleAddAssignmentsClicked}
              size="sm"
              disabled={!addAssignmentEnabled}
            >
              {t("common.schoolCandidateModel.addAssignments")}
            </Button>
          </Col>
          <Col md={2} className="pt-2">
            <Button
              onClick={handleAssignHouseNumberInstitutionClicked}
              className="mt-1"
              size="sm"
              disabled={!addHouseNumberInstitutionEnabled}
            >
              {t("common.schoolCandidateModel.addHouseNumberInstitution")}
            </Button>
          </Col>
        </Row>
      </FormCard>
      <ListTable
        tableProps={tableProps}
        onClickDownload={(exportType: ExportTypeEnum) =>
          handleDownloadClicked(exportType)
        }
        showDownload
        pageSizeOptions={[50, 100, 200, 300, 500, 1000, 1500, 2000, 6000]}
      />
      <ConfirmModal
        title={t("common.approve")}
        confirmState={confirmUnassign}
        confirmMessage={t("common.schoolCandidateModel.confirmUnassign")}
        successMesage={t("common.saveSuccess")}
        errorMessage={t("common.error")}
        onClickClose={() => setConfirmUnassign(undefined)}
        onClickConfirm={handleClearAssignmentsClicked}
      />
    </ContentArea>
  );
}
